<template>
    <plain-layout v-if="isPageReady && isReferralMade" :isPlain="true" :showTerms="false" printClass="print-referral-summary">
        <common-content-spacer height="small">
            <common-page-header :heading="$t('pages.referrals.new.steps.confirmation.request_sent')"></common-page-header>

            <common-page-plain :hasLargePadding="true">
                <referrals-confirmation :heading="$t('pages.referrals.new.steps.confirmation.heading')" type="success">
                    <template #content>
                        <common-summary>
                            <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.patient_name')">
                                <common-user-name :user="referral.patient"></common-user-name>
                            </common-summary-item>

                            <common-summary-item>
                                <common-date :date="selectedSlot.date" :copy="selectedSlot.date.format('dddd')">
                                    <template #time>
                                        <common-slots :slots="[selectedSlot.slot]"></common-slots>
                                    </template>
                                </common-date>
                            </common-summary-item>

                            <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.diagnosis')">
                                {{ referral.diagnosis }}
                            </common-summary-item>

                            <common-summary-item
                                :label="$t('pages.referrals.new.steps.confirmation.summary.to')"
                                :isInline="true"
                            >
                                <common-user-name :user="physician"></common-user-name>

                                <br />

                                <common-location-address :location="location"></common-location-address>

                                <p>
                                    {{ $t('pages.referrals.new.steps.confirmation.summary.tel', { phone: physician.phone }) }}
                                </p>
                            </common-summary-item>
                        </common-summary>
                    </template>
                </referrals-confirmation>
            </common-page-plain>
        </common-content-spacer>
    </plain-layout>

    <plain-layout v-else-if="isPageReady" :isPlain="true" :showTerms="false">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageHeading">
                    <template #right>
                        <referrals-new-back
                            :route="{
                                name: 'referrals.new.results',
                                query: $route.params.originalQuery || $route.query,
                                params: { existingReferral: $route.params.existingReferral },
                            }"
                            :results="[physician]"
                        ></referrals-new-back>
                    </template>
                </common-page-header>
            </common-content-spacer>

            <common-page-split>
                <template #aside>
                    <common-content-spacer height="medium">
                        <practice-member-card
                            v-if="$route.query.practiceType === PracticeTypeEnum.PHYSICIAN"
                            :physician="physician"
                            :isStandalone="false"
                            :isUserProfileLinkEnabled="$route.name !== 'ehr.referrals.new.make'"
                        ></practice-member-card>
                        <practice-card
                            v-if="$route.query.practiceType === PracticeTypeEnum.ANCILLARY"
                            :practice="ancillaryPractice"
                            :isStandalone="false"
                            :isUserProfileLinkEnabled="$route.name !== 'ehr.referrals.new.make'"
                        ></practice-card>
                        <common-content-group>
                            <common-content-spacer height="medium">
                                <common-icon-info icon="location" size="huge">
                                    <common-location-address
                                        :location="location"
                                        :isNameDisplayed="true"
                                    ></common-location-address>
                                </common-icon-info>

                                <common-icon-info :href="`mailto:${physician.email}`" icon="email" size="huge">
                                    {{ physician.email || ancillaryPractice.email }}
                                </common-icon-info>
                            </common-content-spacer>
                        </common-content-group>
                    </common-content-spacer>
                </template>

                <template #main>
                    <template v-if="isSlotSelected">
                        <common-content-spacer height="large">
                            <form-wrapper>
                                <form-row align="center">
                                    <form-cell width="half">
                                        <common-date
                                            :date="selectedSlot.date"
                                            :copy="selectedSlot.date.format('dddd')"
                                        ></common-date>
                                    </form-cell>

                                    <form-cell width="half">
                                        <common-icon-info icon="time" size="gigantic">
                                            <common-slots :slots="[selectedSlot.slot]"></common-slots>
                                        </common-icon-info>
                                    </form-cell>
                                </form-row>
                            </form-wrapper>

                            <referrals-new-make-form
                                :selectedSlot="selectedSlot.slot"
                                :practice="practice"
                                :location="location"
                                :ehr="true"
                                @startOver="selectSlot(null)"
                                @sent="markReferralAsMade($event)"
                            ></referrals-new-make-form>
                        </common-content-spacer>
                    </template>

                    <template v-else>
                        <common-content-wrapper width="huge">
                            <referrals-new-schedule
                                :physician="physician"
                                :practice="practice"
                                :location="location"
                                @selectSlot="selectSlot($event)"
                            ></referrals-new-schedule>
                        </common-content-wrapper>
                    </template>
                </template>
            </common-page-split>
        </common-content-spacer>
    </plain-layout>
</template>

<script>
    import { importComponents } from '~/helpers'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsNewPageMixin from 'mixins/ReferralsNewPageMixin'
    import PlainLayout from 'components/layouts/PlainLayout'
    import { composeDisplayName } from 'components/common/CommonUserName'
    import PhysiciansAPI from 'api/PhysiciansAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import ReferralsNewMakeForm from 'components/forms/ReferralsNewMakeForm'
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import ReferralsNewSchedule from 'components/page-elements/referrals/ReferralsNewSchedule'
    import PracticeCard from 'components/page-elements/practice/PracticeCard'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import UserTypeEnum from '@/enums/UserTypeEnum'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ReferralsNewPageMixin],

        components: {
            PlainLayout,
            ReferralsNewMakeForm,
            PracticeMemberCard,
            PracticeCard,
            ReferralsNewSchedule,
            ...importComponents(require.context('components/form-elements')),
        },

        data() {
            return {
                physician: {},
                ancillaryPractice: {},
                selectedSlot: null,
                isReferralMade: false,
                PracticeTypeEnum: PracticeTypeEnum,
            }
        },

        loadData(self) {
            if (self.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                return {
                    ancillaryPractice: PracticesAPI.getPracticeById(self.$route.query.practiceId),
                }
            } else {
                return {
                    physician: PhysiciansAPI.getPhysicianData({
                        userId: self.$auth.getActiveMember()?.id ? self.$auth.getActiveMember()?.id : self.$auth.getUser().id,
                        physicianId: self.$route.query.physicianId,
                    }),
                }
            }
        },

        computed: {
            pageHeading() {
                const practiceType = this.$route.query.practiceType

                return this.$t(`pages.referrals.new.steps.schedule.heading.${practiceType}`, {
                    name:
                        this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                            ? this.ancillaryPractice.name
                            : composeDisplayName(this.physician),
                })
            },

            isSlotSelected() {
                return this.selectedSlot
            },

            practice() {
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    return this.ancillaryPractice
                } else {
                    return this.$get(this.physician, UserTypeEnum.PRACTICE)
                }
            },

            location() {
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    const mainLocation = {
                        address: this.ancillaryPractice.address,
                        city: this.ancillaryPractice.city,
                        state: this.ancillaryPractice.state,
                        zip: this.ancillaryPractice.zip,
                        isAvailabilitySet: this.ancillaryPractice.isAvailabilitySet,
                        id: 0,
                    }
                    const locations = [mainLocation, ...this.ancillaryPractice.locations]
                    return locations.find((item) => item.id === parseInt(this.$route.query.practiceLocationId))
                } else {
                    return this.$get(this.practice, 'locations', []).find(
                        ({ id: locationId }) => locationId === parseInt(this.$route.query.practiceLocationId),
                    )
                }
            },
        },

        methods: {
            selectSlot(slot) {
                this.selectedSlot = slot
            },

            markReferralAsMade(referral) {
                this.referral = referral
                this.isReferralMade = true
            },

            printSummary() {
                window.print()
            },

            resetPageData() {
                this.isReferralMade = false
                this.selectedSlot = null
            },
        },

        watch: {
            '$route.query'() {
                this.resetPageData()
            },
        },
    }
</script>
